import React, { useMemo } from 'react'
import { observer } from 'mobx-react'
import { Alert, Box, Typography } from '@mui/material'
import moment from 'moment'

import { useStores } from 'admin/hooks'

const WeekdayCalendarHeatmap = () => {
  const { reportStore } = useStores()

  const model = useMemo(() => {
    const counts = new Map()
    ;(reportStore.driverRouteRows || []).forEach((row) => {
      const d = `${row.date || ''}`
      if (!d) return
      counts.set(d, (counts.get(d) || 0) + 1)
    })

    if (!counts.size) return { cells: [], maxCount: 0 }

    const dates = Array.from(counts.keys()).sort()
    const start = moment(dates[0]).startOf('week')
    const end = moment(dates[dates.length - 1]).endOf('week')
    const maxCount = Math.max(...Array.from(counts.values()))

    const cells = []
    for (let cursor = start.clone(); cursor.isSameOrBefore(end); cursor.add(1, 'day')) {
      const dateKey = cursor.format('YYYY-MM-DD')
      const count = counts.get(dateKey) || 0
      const weekIndex = cursor.diff(start, 'weeks')
      const dayIndex = cursor.day()
      cells.push({ dateKey, count, weekIndex, dayIndex })
    }

    return { cells, maxCount }
  }, [reportStore.driverRouteRows])

  if (!model.cells.length)
    return (
      <Alert
        severity="info"
        sx={{ mb: 2 }}
      >
        No calendar pattern data available.
      </Alert>
    )

  const selectedDate = reportStore.driverRouteQuery.selectedDate || ''

  return (
    <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, mb: 1 }}
      >
        Weekday calendar pattern
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: 'block', color: 'text.secondary', mb: 1 }}
      >
        Click a day to drill into rows/map. Click again to clear.
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateRows: 'repeat(7, 16px)',
            gridAutoFlow: 'column',
            gridAutoColumns: '16px',
            gap: '2px',
            minWidth: 400,
          }}
        >
          {model.cells.map((cell) => {
            const intensity = model.maxCount ? cell.count / model.maxCount : 0
            const bg = cell.count ? `rgba(25, 118, 210, ${Math.max(0.18, intensity)})` : '#f5f5f5'
            const active = selectedDate === cell.dateKey
            return (
              <Box
                key={cell.dateKey}
                title={`${cell.dateKey}: ${cell.count} stop(s)`}
                onClick={() =>
                  reportStore.updateDriverRouteFilter(
                    'selectedDate',
                    active ? '' : cell.dateKey,
                    true,
                  )
                }
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: bg,
                  border: active ? '2px solid #ef6c00' : '1px solid #e0e0e0',
                  cursor: 'pointer',
                  gridColumn: cell.weekIndex + 1,
                  gridRow: cell.dayIndex + 1,
                }}
              />
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export default observer(WeekdayCalendarHeatmap)
